import { boatContains, buildBoats, deckY, type Boat } from './boats';
import { bridgeShapes } from './bridging';
import { buildTerrain, type BoatDef, type LevelDef, type LevelStyle, type Material, type Shape } from './levels';
import { clamp, mulberry32 } from './rng';
import type { Terrain } from './terrain';

/**
 * Parameterised map families. A MapSpec (family + seed + numbers) deterministically becomes a
 * LevelDef: the family lays out ground, boats and props from the parameters; spawns, extra
 * spawns and floatie spots are then found by scanning the rasterised field for places a mole
 * can actually stand. The catalogue (catalog.json) is a list of specs that survived bot testing
 * (scripts/mapgen-eval.ts); anything else can still be played from an inline `gen:` id.
 */

export type Family = 'rafts' | 'barge' | 'ledges' | 'rooftops' | 'trench' | 'moon' | 'islands' | 'wreck';
export const FAMILIES: readonly Family[] = ['rafts', 'barge', 'ledges', 'rooftops', 'trench', 'moon', 'islands', 'wreck'];

export interface MapSpec {
  family: Family;
  seed: number;
  params: Record<string, number>;
}

export interface ParamDef {
  min: number;
  max: number;
  /** Rounded to whole numbers (counts, pixel sizes, flags). */
  int?: boolean;
}

interface Built {
  width: number;
  height: number;
  waterY: number | null;
  shapes: Shape[];
  boats: BoatDef[];
  gravity: number;
  stamina: number;
  jump: number;
  style: LevelStyle;
}

export interface FamilyDef {
  name: string;
  blurb: string;
  space: Record<string, ParamDef>;
  build(p: Record<string, number>, rng: () => number): Built;
}

type Rng = () => number;
const HEAD = 56; // UNIT_H, kept local so this module does not pull in the sim

// ---------- palettes ----------

const P = {
  harborDay: { sky: ['#38a8ea', '#d9f3ff'], far: '#8ccfe4', near: '#52ad6e', water: 'rgba(36,150,226,0.82)', deep: '#0d4f88', pit: '#0a3348', horizon: 'islands' },
  harborDusk: { sky: ['#4a2d6b', '#f0a070'], far: '#7a5a8a', near: '#4e3a6a', water: 'rgba(60,70,140,0.85)', deep: '#15102e', pit: '#15102e', horizon: 'islands' },
  lagoon: { sky: ['#2f9fe0', '#dcf4ff'], far: '#8ad0e4', near: '#5db57a', water: 'rgba(34,158,230,0.82)', deep: '#0b4a80', pit: '#0b3a55', horizon: 'islands' },
  storm: { sky: ['#3d4a5c', '#9fb3c4'], far: '#5e7187', near: '#44546a', water: 'rgba(40,80,110,0.9)', deep: '#132231', pit: '#132231', horizon: 'storm' },
  nightSea: { sky: ['#0b1224', '#2c3e6b'], far: '#2b3a5c', near: '#1c2742', water: 'rgba(20,40,90,0.9)', deep: '#060a16', pit: '#060a16', horizon: 'storm' },
  mesas: { sky: ['#c2663a', '#f3c58a'], far: '#9c5a3c', near: '#6e3f2b', water: 'transparent', deep: '#1a0d08', pit: '#1a0d08', horizon: 'mesas' },
  mesasNight: { sky: ['#1b1430', '#5a3a6a'], far: '#3a2a4a', near: '#2a1e38', water: 'transparent', deep: '#0a0610', pit: '#0a0610', horizon: 'mesas' },
  city: { sky: ['#2b1f4e', '#d8798a'], far: '#4a3a6b', near: '#2f2446', water: 'transparent', deep: '#0b0912', pit: '#0b0912', horizon: 'city' },
  cityDay: { sky: ['#4a90c8', '#dbeaf5'], far: '#7e95ad', near: '#56687c', water: 'transparent', deep: '#10151c', pit: '#10151c', horizon: 'city' },
  dunes: { sky: ['#6b7b8c', '#d8cfb8'], far: '#8a7b63', near: '#5f5440', water: 'transparent', deep: '#140f0a', pit: '#140f0a', horizon: 'dunes' },
  dunesDusk: { sky: ['#3a2f4a', '#e08a5a'], far: '#7a5a4a', near: '#4e3a30', water: 'transparent', deep: '#120b08', pit: '#120b08', horizon: 'dunes' },
  moon: { sky: ['#05060f', '#1a2140'], far: '#3a3f55', near: '#262a3d', water: 'transparent', deep: '#020308', pit: '#020308', horizon: 'moon' },
} satisfies Record<string, LevelStyle>;

const pickStyle = (list: LevelStyle[], v: number): LevelStyle => list[clamp(Math.round(v), 0, list.length - 1)]!;

// ---------- geometry helpers ----------

const rect = (x: number, y: number, w: number, h: number, m: Material): Shape => ({ kind: 'rect', x: Math.round(x), y: Math.round(y), w: Math.round(w), h: Math.round(h), m });
const circle = (x: number, y: number, r: number, m: Material): Shape => ({ kind: 'circle', x: Math.round(x), y: Math.round(y), r: Math.round(r), m });
const hole = (x: number, y: number, r: number): Shape => ({ kind: 'hole', x: Math.round(x), y: Math.round(y), r: Math.round(r) });
const poly = (pts: [number, number][], m: Material): Shape => ({ kind: 'poly', pts: pts.map(([x, y]) => [Math.round(x), Math.round(y)] as [number, number]), m });

function mirrorShape(s: Shape, W: number): Shape {
  switch (s.kind) {
    case 'rect':
      return { ...s, x: W - s.x - s.w };
    case 'circle':
    case 'hole':
      return { ...s, x: W - s.x };
    case 'poly':
      return { ...s, pts: s.pts.map(([x, y]) => [W - x, y] as [number, number]).reverse() };
    case 'mask':
      return s; // the generator never makes masks
  }
}

function mirrorBoat(b: BoatDef, W: number): BoatDef {
  const stops = b.stops === 'left' ? 'right' : b.stops === 'right' ? 'left' : b.stops;
  return { ...b, id: `${b.id}-r`, x: W - b.x, stops };
}

/** Left-half shapes plus their mirror images; the mirrored copies may be nudged for asymmetry. */
function mirrored(left: Shape[], W: number, rng: Rng, jitter: number): Shape[] {
  const right = left.map((s) => jitterShape(mirrorShape(s, W), rng, jitter));
  return [...left, ...right];
}

function jitterShape(s: Shape, rng: Rng, amount: number): Shape {
  if (amount <= 0) return s;
  const j = () => (rng() - 0.5) * 2 * amount;
  switch (s.kind) {
    case 'rect':
      return { ...s, x: Math.round(s.x + j() * 0.5), y: Math.round(s.y + j() * 0.3), w: Math.max(40, Math.round(s.w * (1 + j() / 400))) };
    case 'circle':
      return { ...s, x: Math.round(s.x + j() * 0.5), r: Math.max(20, Math.round(s.r * (1 + j() / 300))) };
    case 'hole':
      return { ...s, x: Math.round(s.x + j() * 0.6), r: Math.max(20, Math.round(s.r * (1 + j() / 300))) };
    case 'poly': {
      const dx = j() * 0.4;
      const dy = j() * 0.25;
      return { ...s, pts: s.pts.map(([x, y]) => [Math.round(x + dx), Math.round(y + dy)] as [number, number]) };
    }
    case 'mask':
      return s;
  }
}

// ---------- families ----------

export const FAMILY_DEFS: Record<Family, FamilyDef> = {
  rafts: {
    name: 'Rafts',
    blurb: 'Two rafts, open water. Sink the deck under them or knock them off it.',
    space: {
      width: { min: 2200, max: 3200, int: true },
      panels: { min: 4, max: 8, int: true },
      panelW: { min: 80, max: 110, int: true },
      gap: { min: 700, max: 1400, int: true },
      mid: { min: 0, max: 1, int: true },
      midPanels: { min: 2, max: 4, int: true },
      midStops: { min: 0, max: 1, int: true },
      rocks: { min: 0, max: 3, int: true },
      docks: { min: 0, max: 1, int: true },
      palette: { min: 0, max: 2, int: true },
    },
    build(p, rng) {
      const W = p.width!;
      const deck = p.panels! * p.panelW!;
      const gap = clamp(p.gap!, 500, W - 2 * (140 + deck));
      const leftX = W / 2 - gap / 2 - deck / 2;
      const boats: BoatDef[] = [{ id: 'left', x: Math.round(leftX), y: 900, panelW: p.panelW!, panels: p.panels!, stops: 'both' }];
      boats.push(mirrorBoat(boats[0]!, W));
      if (p.mid) boats.push({ id: 'mid', x: W / 2, y: 920, panelW: p.panelW!, panels: p.midPanels!, stops: p.midStops ? 'both' : 'none' });
      const shapes: Shape[] = [];
      const innerL = leftX + deck / 2;
      const innerR = W - innerL;
      for (let i = 0; i < p.rocks!; i++) {
        const r = 50 + rng() * 35;
        // Spread rocks across the water between the rafts, keeping clear of a mid raft.
        const t = (i + 1) / (p.rocks! + 1);
        let x = innerL + 120 + (innerR - innerL - 240) * t;
        if (p.mid && Math.abs(x - W / 2) < p.midPanels! * p.panelW! / 2 + 80) x += x < W / 2 ? -160 : 160;
        shapes.push(circle(x, 1000 + r * 0.55, r, 'rock'));
      }
      if (p.docks) {
        shapes.push(rect(0, 950, 190, 250, 'wood'), rect(160, 910, 30, 40, 'wood'));
        shapes.push(mirrorShape(rect(0, 950, 190, 250, 'wood'), W), mirrorShape(rect(160, 910, 30, 40, 'wood'), W));
      }
      return { width: W, height: 1200, waterY: 940, shapes, boats, gravity: 1, stamina: 1, jump: 1, style: pickStyle([P.harborDay, P.lagoon, P.harborDusk], p.palette!) };
    },
  },

  barge: {
    name: 'Barge',
    blurb: 'One long deck shared by both crews. Nowhere to hide; everything to sink.',
    space: {
      width: { min: 2600, max: 3400, int: true },
      panels: { min: 8, max: 14, int: true },
      panelW: { min: 90, max: 110, int: true },
      dinghies: { min: 0, max: 1, int: true },
      dinghyGap: { min: 150, max: 400, int: true },
      rocks: { min: 0, max: 2, int: true },
      stamina: { min: 1.1, max: 1.5 },
      palette: { min: 0, max: 2, int: true },
    },
    build(p, rng) {
      const W = p.width!;
      const len = p.panels! * p.panelW!;
      const boats: BoatDef[] = [{ id: 'barge', x: W / 2, y: 900, panelW: p.panelW!, panels: p.panels!, stops: 'both' }];
      if (p.dinghies) {
        const x = W / 2 - len / 2 - p.dinghyGap! - 120;
        if (x > 160) {
          const d: BoatDef = { id: 'dinghy', x: Math.round(x), y: 930, panelW: 80, panels: 3, stops: 'both' };
          boats.push(d, mirrorBoat(d, W));
        }
      }
      const shapes: Shape[] = [];
      for (let i = 0; i < p.rocks!; i++) {
        const r = 60 + rng() * 30;
        const s = circle(120 + i * 170, 1000 + r * 0.5, r, 'rock');
        shapes.push(s, mirrorShape(s, W));
      }
      return { width: W, height: 1200, waterY: 940, shapes, boats, gravity: 1, stamina: p.stamina!, jump: 1, style: pickStyle([P.harborDay, P.storm, P.nightSea], p.palette!) };
    },
  },

  ledges: {
    name: 'Ledges',
    blurb: 'Two cliffs over a bottomless drop, maybe a mesa in between. Boots decide arguments.',
    space: {
      width: { min: 2200, max: 3000, int: true },
      cliffW: { min: 500, max: 900, int: true },
      gapW: { min: 160, max: 600, int: true },
      plateau: { min: 0, max: 1, int: true },
      plateauRise: { min: -120, max: 160, int: true },
      steps: { min: 0, max: 1, int: true },
      bump: { min: 0, max: 1, int: true },
      ledges: { min: 0, max: 1, int: true },
      asym: { min: 0, max: 1 },
      palette: { min: 0, max: 1, int: true },
    },
    build(p, rng) {
      const W = p.width!;
      const H = 1200;
      const g = 720;
      let cliffW = p.cliffW!;
      if (!p.plateau) cliffW = Math.max(cliffW, (W - 1400) / 2); // keep the far cliff in throwing range
      const left: Shape[] = [rect(0, g, cliffW, H - g, 'grass')];
      if (p.bump) left.push(poly([[cliffW - 280, g], [cliffW - 100, g - 80], [cliffW, g - 80], [cliffW, g]], 'grass'));
      if (p.steps) left.push(rect(cliffW + 60, 920, 110, 40, 'stone'));
      if (p.ledges) left.push(rect(cliffW + 40, 460, 150, 26, 'stone'));
      const shapes = mirrored(left, W, rng, p.asym! * 120);
      if (p.plateau) {
        const plateauW = Math.max(260, W - 2 * (cliffW + p.gapW!));
        const top = g - p.plateauRise!;
        shapes.push(rect(W / 2 - plateauW / 2, top, plateauW, H - top, 'grass'), circle(W / 2, top - 16, 36, 'rock'));
      }
      return { width: W, height: H, waterY: null, shapes, boats: [], gravity: 1, stamina: 1, jump: 1, style: pickStyle([P.mesas, P.mesasNight], p.palette!) };
    },
  },

  rooftops: {
    name: 'Rooftops',
    blurb: 'Buildings and the streets between them. Zero railings.',
    space: {
      width: { min: 2400, max: 3000, int: true },
      teamW: { min: 420, max: 620, int: true },
      midW: { min: 220, max: 420, int: true },
      gap: { min: 90, max: 200, int: true },
      midCount: { min: 1, max: 3, int: true },
      roofY: { min: 700, max: 820, int: true },
      midRise: { min: -160, max: 160, int: true },
      chimneys: { min: 0, max: 1, int: true },
      antenna: { min: 0, max: 1, int: true },
      ledges: { min: 0, max: 1, int: true },
      asym: { min: 0, max: 1 },
      palette: { min: 0, max: 1, int: true },
    },
    build(p, rng) {
      const W = p.width!;
      const H = 1200;
      const n = p.midCount!;
      let midW = p.midW!;
      let gap = p.gap!;
      const total = () => 2 * p.teamW! + n * midW + (n + 1) * gap;
      while (total() > W - 120 && (midW > 160 || gap > 70)) {
        midW = Math.max(160, midW - 20);
        gap = Math.max(70, gap - 10);
      }
      const x0 = (W - total()) / 2;
      const roof = p.roofY!;
      const left: Shape[] = [rect(x0, roof, p.teamW!, H - roof, 'stone')];
      if (p.chimneys) left.push(rect(x0 + 60, roof - 60, 70, 60, 'metal'));
      if (p.ledges) left.push(rect(x0 + p.teamW! + gap / 2 - 90, roof - 180, 180, 16, 'metal'));
      const shapes = mirrored(left, W, rng, p.asym! * 120);
      for (let i = 0; i < n; i++) {
        const x = x0 + p.teamW! + gap + i * (midW + gap);
        const centre = (n - 1) / 2;
        const k = n === 1 ? 1 : 1 - Math.abs(i - centre) / (centre + 1);
        const top = roof - p.midRise! * k;
        shapes.push(rect(x, top, midW, H - top, i % 2 === 0 ? 'metal' : 'stone'));
        if (p.antenna && i === Math.floor(centre)) shapes.push(rect(x + midW / 2 - 10, top - 120, 20, 120, 'metal'), rect(x + midW / 2 - 90, top - 120, 180, 16, 'metal'));
      }
      return { width: W, height: H, waterY: null, shapes, boats: [], gravity: 1, stamina: 1, jump: 1, style: pickStyle([P.city, P.cityDay], p.palette!) };
    },
  },

  trench: {
    name: 'Trench',
    blurb: 'Flat dirt with a ditch down the middle. Long walks; everything crumbles.',
    space: {
      width: { min: 2600, max: 3400, int: true },
      trenchW: { min: 380, max: 800, int: true },
      depth: { min: 160, max: 320, int: true },
      ramp: { min: 0, max: 1, int: true },
      crates: { min: 0, max: 2, int: true },
      mounds: { min: 0, max: 2, int: true },
      holes: { min: 0, max: 2, int: true },
      stamina: { min: 1.2, max: 1.6 },
      asym: { min: 0, max: 1 },
      palette: { min: 0, max: 1, int: true },
    },
    build(p, rng) {
      const W = p.width!;
      const H = 1300;
      const g = 760;
      const edge = W / 2 - p.trenchW! / 2;
      const left: Shape[] = [rect(0, g, edge, H - g, 'grass')];
      if (p.ramp) left.push(poly([[edge, g], [edge, g + p.depth!], [edge + 110, g + p.depth!]], 'grass'));
      for (let i = 0; i < p.crates!; i++) left.push(rect(460 + i * 520, g - 56, 160, 56, 'wood'));
      for (let i = 0; i < p.mounds!; i++) left.push(circle(220 + i * 520, g - 20, 42 + rng() * 14, 'rock'));
      for (let i = 0; i < p.holes!; i++) left.push(hole(edge - 200 - i * 300, g + 30, 44 + rng() * 16));
      const shapes = mirrored(left, W, rng, p.asym! * 120);
      shapes.push(rect(edge, g + p.depth!, p.trenchW!, H - g - p.depth!, 'grass'));
      return { width: W, height: H, waterY: null, shapes, boats: [], gravity: 1, stamina: p.stamina!, jump: 1, style: pickStyle([P.dunes, P.dunesDusk], p.palette!) };
    },
  },

  moon: {
    name: 'Moon',
    blurb: 'Low gravity, long walks, high jumps. Throws carry forever; so does a bad landing.',
    space: {
      width: { min: 2800, max: 3600, int: true },
      craterW: { min: 400, max: 800, int: true },
      depth: { min: 180, max: 320, int: true },
      mounds: { min: 0, max: 2, int: true },
      holes: { min: 0, max: 3, int: true },
      platform: { min: 0, max: 1, int: true },
      gravity: { min: 0.4, max: 0.6 },
      stamina: { min: 1.5, max: 2.0 },
      jump: { min: 1.3, max: 1.7 },
      asym: { min: 0, max: 1 },
    },
    build(p, rng) {
      const W = p.width!;
      const H = 1400;
      const g = 860;
      const edge = W / 2 - p.craterW! / 2;
      const left: Shape[] = [
        rect(0, g, edge, H - g, 'regolith'),
        poly([[edge, g], [edge, g + p.depth!], [edge + 150, g + p.depth!], [edge + 150, g + p.depth! * 0.6]], 'regolith'),
      ];
      for (let i = 0; i < p.mounds!; i++) left.push(circle(560 + i * 560, g - 40, 55 + rng() * 25, 'regolith'));
      for (let i = 0; i < p.holes!; i++) left.push(hole(320 + i * 420, g + 50, 55 + rng() * 35));
      const shapes = mirrored(left, W, rng, p.asym! * 120);
      shapes.push(rect(edge, g + p.depth!, p.craterW!, H - g - p.depth!, 'regolith'));
      if (p.platform) shapes.push(rect(W / 2 - 150, 520, 300, 30, 'metal'));
      return { width: W, height: H, waterY: null, shapes, boats: [], gravity: p.gravity!, stamina: p.stamina!, jump: p.jump!, style: P.moon };
    },
  },

  islands: {
    name: 'Islands',
    blurb: 'A home island each and stepping stones between. The tide keeps what it catches.',
    space: {
      width: { min: 2400, max: 3200, int: true },
      islandW: { min: 420, max: 700, int: true },
      gap: { min: 700, max: 1400, int: true },
      islandTop: { min: 820, max: 900, int: true },
      stones: { min: 1, max: 3, int: true },
      stoneR: { min: 60, max: 110, int: true },
      stoneRise: { min: 30, max: 80, int: true },
      hill: { min: 0, max: 1, int: true },
      palette: { min: 0, max: 2, int: true },
    },
    build(p, rng) {
      const W = p.width!;
      const H = 1200;
      const top = p.islandTop!;
      const w = p.islandW!;
      // Home islands sit so their inner shores are `gap` apart (within throwing reach), never off the edge.
      const gap = Math.min(p.gap!, W - 2 * (w + 60));
      const cx = W / 2 - gap / 2 - w / 2;
      const left: Shape[] = [rect(cx - w / 2, top, w, H - top, 'grass'), circle(cx - w / 2, top + 50, 50, 'grass'), circle(cx + w / 2, top + 50, 50, 'grass')];
      if (p.hill) left.push(circle(cx - w * 0.3, top + 10, 44 + rng() * 16, 'rock'));
      const shapes = mirrored(left, W, rng, 0);
      const L = cx + w / 2 + 50;
      const R = W - L;
      for (let i = 0; i < p.stones!; i++) {
        const x = L + ((R - L) * (i + 1)) / (p.stones! + 1);
        const r = p.stoneR!;
        shapes.push(circle(x, 1000 + r - p.stoneRise!, r, 'rock'), rect(x - r * 0.55, 1000 - p.stoneRise!, r * 1.1, p.stoneRise! + 30, 'rock'));
      }
      return { width: W, height: H, waterY: 940, shapes, boats: [], gravity: 1, stamina: 1, jump: 1, style: pickStyle([P.lagoon, P.harborDay, P.harborDusk], p.palette!) };
    },
  },

  wreck: {
    name: 'Shipwreck',
    blurb: 'A listing hull between a dock and a rocky shore. Slippery, and nothing here is level.',
    space: {
      width: { min: 2400, max: 3000, int: true },
      hullLen: { min: 700, max: 1000, int: true },
      tilt: { min: 60, max: 160, int: true },
      dock: { min: 0, max: 1, int: true },
      mast: { min: 0, max: 1, int: true },
      mirror: { min: 0, max: 1, int: true },
      shoreW: { min: 400, max: 650, int: true },
      rocks: { min: 0, max: 2, int: true },
      palette: { min: 0, max: 1, int: true },
    },
    build(p, rng) {
      const W = p.width!;
      const H = 1200;
      const dock = (): Shape[] => {
        const len = Math.min(0.2 * W, 480);
        return [rect(0, 940, len, H - 940, 'wood'), rect(len - 30, 900, 30, 40, 'wood'), circle(70, 1030, 80, 'rock')];
      };
      const shore = (): Shape[] => {
        const sw = p.shoreW!;
        return [poly([[0, 900], [sw * 0.55, 860], [sw * 0.85, 880], [sw, 950], [sw, H], [0, H]], 'grass'), circle(sw * 0.3, 870, 40, 'rock')];
      };
      const leftSide = p.dock ? dock() : shore();
      const rightSide = (p.mirror ? leftSide : p.dock ? shore() : dock()).map((s) => mirrorShape(s, W));
      const shapes = [...leftSide, ...rightSide];
      const x0 = W / 2 - p.hullLen! / 2;
      const x1 = W / 2 + p.hullLen! / 2;
      shapes.push(poly([[x0, 900 + p.tilt! / 2], [x1, 900 - p.tilt! / 2], [x1 + 40, 1100], [x0 - 40, 1100]], 'wood'));
      if (p.mast) {
        const mx = x1 - p.hullLen! * 0.35;
        shapes.push(rect(mx - 13, 500, 26, 400, 'wood'), rect(mx - 73, 500, 146, 24, 'wood'));
      }
      for (let i = 0; i < p.rocks!; i++) {
        const r = 60 + rng() * 35;
        shapes.push(circle(x1 + 160 + i * 150, 1000 + r * 0.45, r, 'rock'));
      }
      return { width: W, height: H, waterY: 940, shapes, boats: [], gravity: 1, stamina: 1, jump: 1, style: pickStyle([P.storm, P.nightSea], p.palette!) };
    },
  },
};

// ---------- specs ----------

export function randomSpec(family: Family, rng: Rng): MapSpec {
  const def = FAMILY_DEFS[family];
  const params: Record<string, number> = {};
  for (const [k, d] of Object.entries(def.space)) params[k] = sample(d, rng);
  return { family, seed: Math.floor(rng() * 1e9), params };
}

function sample(d: ParamDef, rng: Rng): number {
  const v = d.min + rng() * (d.max - d.min);
  return d.int ? Math.round(v) : Math.round(v * 100) / 100;
}

/** A nearby spec: every number nudged a little, one or two of them more, and a fresh seed. */
export function mutateSpec(spec: MapSpec, rng: Rng, amount = 0.2): MapSpec {
  const def = FAMILY_DEFS[spec.family];
  const params: Record<string, number> = {};
  const keys = Object.keys(def.space);
  const big = new Set<string>([keys[Math.floor(rng() * keys.length)]!]);
  if (rng() < 0.5) big.add(keys[Math.floor(rng() * keys.length)]!);
  for (const k of keys) {
    const d = def.space[k]!;
    const span = d.max - d.min;
    const cur = spec.params[k] ?? sample(d, rng);
    const scale = big.has(k) ? amount * 2 : amount * 0.5;
    let v = cur + (rng() - 0.5) * 2 * span * scale;
    if (d.int && d.max - d.min <= 3 && big.has(k)) v = sample(d, rng); // flags and small counts: re-roll instead of drifting
    v = clamp(v, d.min, d.max);
    params[k] = d.int ? Math.round(v) : Math.round(v * 100) / 100;
  }
  return { family: spec.family, seed: Math.floor(rng() * 1e9), params };
}

/** Inline id: `gen:<family>:<seed>:<k=v,...>`; playable without the catalogue. */
export function encodeSpec(spec: MapSpec): string {
  const kv = Object.entries(spec.params).map(([k, v]) => `${k}=${v}`).join(',');
  return `gen:${spec.family}:${spec.seed}:${kv}`;
}

export function decodeSpec(id: string): MapSpec | null {
  const m = /^gen:([a-z]+):(\d+):(.*)$/.exec(id);
  if (!m) return null;
  const family = m[1] as Family;
  if (!FAMILY_DEFS[family]) return null;
  const params: Record<string, number> = {};
  if (m[3]) {
    for (const part of m[3].split(',')) {
      const [k, v] = part.split('=');
      const n = Number(v);
      if (!k || !Number.isFinite(n)) return null;
      params[k] = n;
    }
  }
  // Fill anything missing so an old id still builds.
  const rng = mulberry32(Number(m[2]) >>> 0);
  for (const [k, d] of Object.entries(FAMILY_DEFS[family].space)) if (!(k in params)) params[k] = sample(d, rng);
  return { family, seed: Number(m[2]), params };
}

// ---------- placing moles ----------

interface Field {
  t: Terrain;
  boats: Boat[];
  waterY: number | null;
  width: number;
}

function solidAt(f: Field, x: number, y: number): boolean {
  if (f.t.isSolid(x, y)) return true;
  for (const b of f.boats) if (boatContains(b, x, y)) return true;
  return false;
}

/** Top-most surface in column x: terrain or a deck. */
function surfaceAt(f: Field, x: number): number | null {
  let y: number | null = null;
  const ty = f.t.surfaceY(x, 0);
  if (ty < f.t.h) y = ty;
  for (const b of f.boats) {
    const d = deckY(b, x);
    if (d !== null && (y === null || d < y)) y = d;
  }
  return y;
}

/** Y a mole can stand at in column x (flat enough, headroom, dry), or null. */
function standAt(f: Field, x: number): number | null {
  const y = surfaceAt(f, x);
  if (y === null) return null;
  if (f.waterY !== null && y > f.waterY - 8) return null;
  for (let dy = 8; dy <= HEAD + 8; dy += 8) if (solidAt(f, x - 10, y - dy) || solidAt(f, x + 10, y - dy)) return null;
  const l = surfaceAt(f, x - 12);
  const r = surfaceAt(f, x + 12);
  if (l === null || r === null || Math.abs(l - y) > 8 || Math.abs(r - y) > 8) return null;
  return y;
}

/** Distance to the nearest drop-off either side, up to 120. */
function margin(f: Field, x: number, y: number): number {
  let m = 120;
  for (const dir of [-1, 1]) {
    for (let d = 16; d <= 120; d += 8) {
      const s = surfaceAt(f, x + dir * d);
      if (s === null || s > y + 40) {
        m = Math.min(m, d);
        break;
      }
    }
  }
  return m;
}

function pickSpots(f: Field, x0: number, x1: number, count: number, order: 'asc' | 'desc', used: [number, number][] = []): [number, number][] {
  const cands: { x: number; y: number; score: number }[] = [];
  for (let x = Math.max(28, Math.round(x0)); x <= Math.min(f.width - 28, x1); x += 8) {
    const y = standAt(f, x);
    if (y === null) continue;
    const m = margin(f, x, y);
    if (m < 24) continue;
    cands.push({ x, y, score: Math.min(m, 72) });
  }
  cands.sort((a, b) => b.score - a.score || a.x - b.x);
  const picked: [number, number][] = [];
  for (const gap of [120, 90, 64]) {
    for (const c of cands) {
      if (picked.length >= count) break;
      const near = (p: [number, number]) => Math.abs(p[0] - c.x) < gap && Math.abs(p[1] - c.y) < 90;
      if (picked.some(near) || used.some(near)) continue;
      picked.push([c.x, c.y]);
    }
    if (picked.length >= count) break;
  }
  picked.sort((a, b) => (order === 'asc' ? a[0] - b[0] : b[0] - a[0]));
  return picked;
}

/**
 * Where N teams start: the map is cut into N zones from left to right and each team gets
 * standing spots in its own zone (nearest the middle of the zone first), so four to eight
 * teams spread across the whole map instead of piling onto the authored middle spots. A zone
 * with no standing room (open water) borrows the nearest free spots outside it. Two teams keep
 * the authored left and right spawns; three keep the authored middle too.
 */
export function spawnsForTeams(level: LevelDef, teams: number, perTeam: number): [number, number][][] {
  if (teams <= 3) return level.spawns.slice(0, teams).map((s) => s.slice());
  const t = buildTerrain(level);
  const f: Field = { t, boats: buildBoats(level.boats), waterY: level.waterY, width: level.width };
  const W = level.width;
  const zone = W / teams;
  const used: [number, number][] = [];
  const out: [number, number][][] = [];
  for (let i = 0; i < teams; i++) {
    const x0 = i * zone + 24;
    const x1 = (i + 1) * zone - 24;
    const centre = (x0 + x1) / 2;
    let spots = pickSpots(f, x0, x1, perTeam, 'asc', used);
    if (spots.length < perTeam) {
      // Not enough room in the zone: take the nearest free spots anywhere.
      const more = pickSpots(f, 0, W, perTeam * 3, 'asc', [...used, ...spots])
        .sort((p, q) => Math.abs(p[0] - centre) - Math.abs(q[0] - centre))
        .slice(0, perTeam - spots.length);
      spots = [...spots, ...more];
    }
    spots.sort((p, q) => Math.abs(p[0] - centre) - Math.abs(q[0] - centre));
    used.push(...spots);
    out.push(spots);
  }
  return out;
}

/** Four spots for a side, innermost first (two-mole teams start nearest the fight), widening the zone toward the middle when a side is narrow (a barge). */
function teamSpots(f: Field, side: 0 | 1, count: number): [number, number][] {
  const W = f.width;
  let best: [number, number][] = [];
  for (let frac = 0.34; frac <= 0.49; frac += 0.05) {
    const spots = side === 0 ? pickSpots(f, 0.03 * W, frac * W, count, 'desc') : pickSpots(f, (1 - frac) * W, 0.97 * W, count, 'asc');
    if (spots.length > best.length) best = spots;
    if (best.length >= count) break;
  }
  return best;
}

function floatSpots(f: Field, side: 0 | 1 | 2, count: number, spawns: [number, number][]): [number, number][] {
  const W = f.width;
  if (f.waterY !== null) {
    const y = f.waterY - 14;
    const [a, b] = side === 0 ? [0.08 * W, 0.5 * W] : side === 1 ? [0.5 * W, 0.92 * W] : [0.3 * W, 0.7 * W];
    const cands: number[] = [];
    for (let x = a; x <= b; x += 10) {
      if (f.t.surfaceY(x, 0) < f.waterY + 20) continue; // something solid at or above the waterline here
      if (f.boats.some((bt) => deckY(bt, x - 60) !== null || deckY(bt, x) !== null || deckY(bt, x + 60) !== null)) continue;
      if (spawns.some((s) => Math.abs(s[0] - x) < 70)) continue;
      cands.push(Math.round(x));
    }
    // Beside your own side: nearest the team's spawns, spread out.
    const home = spawns.length ? spawns.reduce((s, p) => s + p[0], 0) / spawns.length : side === 0 ? 0.2 * W : side === 1 ? 0.8 * W : 0.5 * W;
    cands.sort((p, q) => Math.abs(p - home) - Math.abs(q - home));
    const out: [number, number][] = [];
    for (const x of cands) {
      if (out.length >= count) break;
      if (out.some((o) => Math.abs(o[0] - x) < 90)) continue;
      out.push([x, y]);
    }
    return out;
  }
  // Over a pit: lower ledges on your side, else any spare standing spot.
  const [a, b] = side === 0 ? [0.03 * W, 0.5 * W] : side === 1 ? [0.5 * W, 0.97 * W] : [0.3 * W, 0.7 * W];
  const spots = pickSpots(f, a, b, count + 6, side === 1 ? 'desc' : 'asc', spawns);
  const ground = spawns.length ? Math.min(...spawns.map((s) => s[1])) : 0;
  const lower = spots.filter((s) => s[1] > ground + 50);
  const chosen = (lower.length >= count ? lower : [...lower, ...spots.filter((s) => !lower.includes(s))]).slice(0, count);
  return chosen;
}

export interface GenMeta {
  id: string;
  name: string;
  blurb?: string;
}

/**
 * Spots for a side on its widest flat run of ground (the lowest one when two are as wide): where
 * a war map's crew stands, rather than on whatever ledge scores best. Innermost first.
 */
function platformSpots(f: Field, side: 0 | 1, count: number): [number, number][] {
  const W = f.width;
  const [x0, x1] = side === 0 ? [0.04 * W, 0.4 * W] : [0.6 * W, 0.96 * W];
  const target = side === 0 ? 0.26 * W : 0.74 * W;
  interface Run {
    xs: number[];
    y: number;
  }
  const runs: Run[] = [];
  let cur: Run | null = null;
  for (let x = Math.max(28, Math.round(x0)); x <= Math.min(W - 28, x1); x += 8) {
    const y = standAt(f, x);
    const ok = y !== null && margin(f, x, y) >= 24;
    if (ok && cur && Math.abs(cur.y - y!) <= 10) cur.xs.push(x);
    else {
      cur = ok ? { xs: [x], y: y! } : null;
      if (cur) runs.push(cur);
    }
  }
  // Wide enough is wide enough (a crew of four needs about 350 px); past that, nearest the
  // middle of the side wins, then the lower run.
  const score = (r: Run): number => Math.min(r.xs.length * 8, 360) - Math.abs((r.xs[0]! + r.xs[r.xs.length - 1]!) / 2 - target) * 0.45;
  runs.sort((p, q) => score(q) - score(p) || q.y - p.y);
  const out: [number, number][] = [];
  for (const run of runs) {
    // Around the middle of the side, a mole's width and a bit apart.
    const xs = [...run.xs].sort((p, q) => Math.abs(p - target) - Math.abs(q - target));
    for (const x of xs) {
      if (out.length >= count) break;
      if (out.every((p) => Math.abs(p[0] - x) >= 88 || Math.abs(p[1] - run.y) > 60)) out.push([x, run.y]);
    }
    if (out.length >= count) break;
  }
  // Innermost first: two-mole crews start nearest the fight.
  return out.sort((p, q) => (side === 0 ? q[0] - p[0] : p[0] - q[0]));
}

/** Where the original stage put a side's crew, snapped to the ground below each marker. Innermost first. */
function markerSpots(f: Field, markers: readonly { x: number; y: number }[], side: 0 | 1, count: number): [number, number][] {
  const out: [number, number][] = [];
  const sorted = [...markers].sort((p, q) => (side === 0 ? q.x - p.x : p.x - q.x));
  for (const m of sorted) {
    const x = Math.round(m.x / 8) * 8;
    const ground = f.t.surfaceY(x, Math.max(0, m.y - 70));
    if (ground >= f.t.h) continue;
    const y = standAt(f, x);
    if (y === null || Math.abs(y - ground) > 12 || margin(f, x, y) < 20) continue;
    if (out.some((p) => Math.abs(p[0] - x) < 80 && Math.abs(p[1] - y) < 80)) continue;
    out.push([x, y]);
    if (out.length >= count) break;
  }
  return out;
}

export interface PlaceOptions {
  /** War maps: stand on the widest flat run of a side, not the best-scoring ledge. */
  platform?: boolean;
  /** War maps with the original stage's spawn markers (px in level space). */
  markers?: { left: { x: number; y: number }[]; right: { x: number; y: number }[] };
}

/**
 * Finds where each team's moles start and where floaties sit, from the ground and boats a level
 * already has: flat, roomy standing spots on either side of the middle. Fills the level's spawn
 * and float lists in place. Used by the generator and by the war maps.
 */
export function placeMoles(level: LevelDef, opts: PlaceOptions = {}): void {
  const t = buildTerrain(level);
  const f: Field = { t, boats: buildBoats(level.boats), waterY: level.waterY, width: level.width };
  let left: [number, number][] = [];
  let right: [number, number][] = [];
  if (opts.markers) {
    left = markerSpots(f, opts.markers.left, 0, 4);
    right = markerSpots(f, opts.markers.right, 1, 4);
  }
  if (left.length < 2) left = opts.platform ? platformSpots(f, 0, 4) : teamSpots(f, 0, 4);
  if (right.length < 2) right = opts.platform ? platformSpots(f, 1, 4) : teamSpots(f, 1, 4);
  // A crew of four needs four spots on its own side, or the overflow would borrow the enemy's.
  if (left.length < 4) left = [...left, ...pickSpots(f, 0.03 * level.width, 0.45 * level.width, 4 - left.length, 'desc', [...left, ...right])];
  if (right.length < 4) right = [...right, ...pickSpots(f, 0.55 * level.width, 0.97 * level.width, 4 - right.length, 'asc', [...left, ...right])];
  let extras = pickSpots(f, 0.38 * level.width, 0.62 * level.width, 4, 'asc', [...left, ...right]);
  if (extras.length < 2) {
    const more = [...pickSpots(f, 0.03 * level.width, 0.5 * level.width, 2, 'desc', [...left, ...right, ...extras]), ...pickSpots(f, 0.5 * level.width, 0.97 * level.width, 2, 'asc', [...left, ...right, ...extras])];
    extras = [...extras, ...more].slice(0, 4);
  }
  level.spawns = [left, right, extras];
  level.floats = [floatSpots(f, 0, 2, left), floatSpots(f, 1, 2, right), floatSpots(f, 2, 1, extras)];
}

/**
 * Lays bridges where the level's two halves are not joined by a walkable route, then places the
 * moles again (the planks make new standing places). Every level gets a way across.
 */
export function ensureCrossing(level: LevelDef, opts: PlaceOptions = {}): void {
  placeMoles(level, opts);
  const crossings = bridgeShapes(level);
  if (crossings.shapes.length === 0 && crossings.boats.length === 0) return;
  level.shapes = [...level.shapes, ...crossings.shapes];
  level.boats = [...level.boats, ...crossings.boats];
  placeMoles(level, opts);
}

/** Build the level for a spec. Deterministic: the same spec always gives the same map. */
export function generateLevel(spec: MapSpec, meta: GenMeta): LevelDef {
  const def = FAMILY_DEFS[spec.family];
  const rng = mulberry32(spec.seed >>> 0);
  const built = def.build(spec.params, rng);
  const level: LevelDef = { id: meta.id, name: meta.name, blurb: meta.blurb ?? def.blurb, ...built, spawns: [[], [], []], floats: [[], [], []] };
  ensureCrossing(level);
  return level;
}

/** Reasons a generated level is unplayable (empty when fine). */
export function levelProblems(level: LevelDef): string[] {
  const out: string[] = [];
  if ((level.spawns[0]?.length ?? 0) < 2) out.push('fewer than two left spawns');
  if ((level.spawns[1]?.length ?? 0) < 2) out.push('fewer than two right spawns');
  if (level.spawns[0]?.length && level.spawns[1]?.length) {
    // The first two spawns are where two-mole teams (the default) actually start.
    const gapX = Math.min(...level.spawns[1]!.slice(0, 2).map((s) => s[0])) - Math.max(...level.spawns[0]!.slice(0, 2).map((s) => s[0]));
    if (gapX < 300) out.push('spawns too close');
    // A full-power grenade carries ~1600 px on flat ground at normal gravity (see range.test.ts); on
    // wide maps both sides can also close in with a turn and a half of walking first.
    const walk = 280 * level.stamina * 1.5;
    if (gapX > 1600 / level.gravity + 2 * walk) out.push('spawns out of throwing range');
  }
  // A chain of pontoons across the water takes the floaties' room; that is the price of a way across.
  const pontoons = level.boats.some((b) => b.id.startsWith('pontoon-'));
  if (!pontoons && ((level.floats[0]?.length ?? 0) < 1 || (level.floats[1]?.length ?? 0) < 1)) out.push('no floatie spot on a side');
  return out;
}

// ---------- names ----------

const NAMES: Record<Family, string[]> = {
  rafts: ['Still Water', 'Dinghy Duel', 'Two Rafts', 'Buoy Line', 'Slack Tide', 'Pontoon Row', 'Cold Harbour', 'Mooring Lights', 'Jetty Jam', 'Flat Calm'],
  barge: ['Long Deck', 'Cargo Row', 'Freight Fight', 'Barge Brawl', 'Low Freeboard', 'Tow Line', 'Hold Fast', 'Flat Bottom', 'Ballast', 'Bollard Bay'],
  ledges: ['Red Shelf', 'Twin Mesas', 'The Notch', 'Dry Gulch', 'Sandstone Step', 'Hoodoo Pass', 'Rim Rock', 'Saddle Gap', 'Goat Ledge', 'Scree Slope'],
  rooftops: ['Skyline', 'Fire Escape', 'Water Towers', 'Penthouse Row', 'Chimney Pots', 'Billboard Alley', 'Antenna Farm', 'Night Shift', 'Block Party', 'Rush Hour'],
  trench: ["No Man's Land", 'Sandbag Line', 'The Ditch', 'Crater Field', 'Dugout', 'Shell Hollow', 'Wire Row', 'Mud Season', 'Front Line', 'Parapet'],
  moon: ['Tranquility', 'Mare Nubium', 'Regolith Run', 'Low Orbit', 'Far Side', 'Crater Rim', 'Sea of Dust', 'Apollo Hollow', 'Lander Site', 'Grey Plain'],
  islands: ['Atoll', 'Stepping Stones', 'Sandbar', 'The Keys', 'Reef Break', 'Three Cays', 'Shoal Water', 'Tide Pools', 'Lagoon Hop', 'Coral Run'],
  wreck: ['Driftwood', 'Salvage', 'Listing Deck', "Crow's Nest", 'Shipbreak', 'Capsized', 'Ghost Hull', 'Rust Bucket', 'Keelhaul', 'Barnacle Bay'],
};
const VARIANTS = ['Dawn', 'Dusk', 'Squall', 'Flood', 'Ebb', 'Fog', 'Gale', 'Calm', 'Ember', 'Frost', 'Echo', 'Drift', 'Spire', 'Hollow', 'Ridge', 'Haze'];

export function familyName(family: Family): string {
  return FAMILY_DEFS[family].name;
}

/** The i-th parent name for a family (wraps around with a numeral). */
export function parentName(family: Family, i: number): string {
  const list = NAMES[family];
  const base = list[i % list.length]!;
  return i >= list.length ? `${base} ${Math.floor(i / list.length) + 1}` : base;
}

export function childName(parent: string, i: number): string {
  return `${parent}: ${VARIANTS[i % VARIANTS.length]}${i >= VARIANTS.length ? ` ${Math.floor(i / VARIANTS.length) + 1}` : ''}`;
}
